/**
 * Single source of truth for candidate profile completion %.
 * Used by client (Profile UI) and server (API enrich).
 *
 * To include a new profile field in completion scoring:
 * 1. Add a rule object to PROFILE_COMPLETION_RULES below.
 * 2. Set sectionId to match the profile section element id in Profile.jsx.
 * 3. Set required: 'always' | 'non_fresher' | 'optional' (optional = only scored if partially filled).
 */

import { isValidIndianMobile } from './phoneUtils.js';

function hasText(value) {
  return String(value ?? '').trim().length > 0;
}

function formatDateOnly(value) {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    const y = value.getFullYear();
    const m = String(value.getMonth() + 1).padStart(2, '0');
    const d = String(value.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isValidEmail(value) {
  const v = (value || '').trim();
  return v.length > 0 && /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(v);
}


function isValidPhone(value) {
  return isValidIndianMobile(value);
}

function isValidSalaryCtc(value, { required = false } = {}) {
  const raw = String(value ?? '').replace(/,/g, '').trim();
  if (!raw) return required ? false : true;
  if (!/^\d+$/.test(raw)) return false;
  return raw.length >= 6;
}

const NOTICE_PERIOD_DAYS = [10, 15, 20, 30, 45, 60, 75, 90];

function isImmediateJoinerAvailability(value) {
  const v = String(value || '').trim().toLowerCase();
  return v === 'immediate' || v === 'immediate joiner' || v.includes('immediate join');
}

function isValidNoticePeriod(value) {
  const v = String(value || '').trim();
  if (!v) return false;
  if (isImmediateJoinerAvailability(v)) return true;
  const match = v.match(/^(\d+)\s*(?:days?)?$/i);
  const days = match ? Number(match[1]) : /^\d+$/.test(v) ? Number(v) : NaN;
  return NOTICE_PERIOD_DAYS.includes(days);
}

function normalizeLanguagesKnown(source) {
  const list = Array.isArray(source) ? source : [];
  const seen = new Set();
  const out = [];
  for (const row of list) {
    const language = String(row?.language || '').trim();
    if (!language || seen.has(language.toLowerCase())) continue;
    seen.add(language.toLowerCase());
    out.push({
      language,
      read: !!row.read,
      write: !!row.write,
      speak: !!row.speak,
      readLevel: row.read ? String(row.readLevel || '').trim() : '',
      writeLevel: row.write ? String(row.writeLevel || '').trim() : '',
      speakLevel: row.speak ? String(row.speakLevel || '').trim() : ''
    });
  }
  return out;
}

function normalizePreferredWorkLocations(form) {
  if (Array.isArray(form.preferredWorkLocations) && form.preferredWorkLocations.length) {
    return form.preferredWorkLocations.map(s => String(s).trim()).filter(Boolean);
  }
  const loc = (form.location || '').trim();
  return loc ? loc.split(/[,;]/).map(s => s.trim()).filter(Boolean) : [];
}

function educationComplete(educational) {
  const rows = Array.isArray(educational) ? educational : [];
  return rows.some(row => {
    const degree = (row.degree || row.qualification || '').trim();
    const institution = (row.institution || row.school || row.college || '').trim();
    return degree && institution;
  });
}

function professionalComplete(professional) {
  const rows = Array.isArray(professional) ? professional : [];
  return rows.some(row => {
    const company = (row.company || row.employer || row.currentCompany || '').trim();
    const designation = (row.designation || row.title || row.role || '').trim();
    const startDate = formatDateOnly(row.startDate);
    return company && designation && startDate;
  });
}

function computeExperienceYearsFromProfessional(professional) {
  const rows = Array.isArray(professional) ? professional : [];
  let totalMonths = 0;
  for (const row of rows) {
    const startIso = formatDateOnly(row.startDate);
    if (!startIso) continue;
    const start = new Date(`${startIso}T12:00:00`);
    const endIso = formatDateOnly(row.endDate) || formatDateOnly(new Date());
    const end = new Date(`${endIso}T12:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) continue;
    totalMonths +=
      (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  }
  if (totalMonths <= 0) return null;
  return Math.round((totalMonths / 12) * 10) / 10;
}

function languagesComplete(languagesKnown) {
  const langs = normalizeLanguagesKnown(languagesKnown);
  if (!langs.length) return false;
  return langs.every(lang => {
    if (!lang.language) return false;
    const hasSkill = lang.read || lang.write || lang.speak;
    if (!hasSkill) return false;
    if (lang.read && !lang.readLevel) return false;
    if (lang.write && !lang.writeLevel) return false;
    if (lang.speak && !lang.speakLevel) return false;
    return true;
  });
}

/** Normalize API candidate or form object into a consistent shape for rules. */
function normalizeProfileForm(candidate, user) {
  if (!candidate) return null;
  const personal = candidate.personal || {};
  const contact = candidate.contact || {};
  return {
    ...candidate,
    name: personal.name || contact.name || candidate.name || user?.name || '',
    email: contact.email || candidate.email || user?.email || '',
    phone: contact.phone || candidate.phone || user?.phone || '',
    personal: {
      name: personal.name || contact.name || candidate.name || user?.name || '',
      dob: formatDateOnly(personal.dob),
      gender: personal.gender || '',
      maritalStatus: personal.maritalStatus || '',
      address: personal.address || '',
      city: personal.city || '',
      state: personal.state || '',
      zip: personal.zip || '',
      country: personal.country || ''
    },
    contact: {
      email: contact.email || candidate.email || user?.email || '',
      phone: contact.phone || candidate.phone || user?.phone || '',
      linkedin: contact.linkedin || '',
      website: contact.website || ''
    },
    isFresher: !!candidate.isFresher,
    languagesKnown: normalizeLanguagesKnown(candidate.languagesKnown)
  };
}

function ruleApplies(rule, form) {
  if (rule.required === 'always') return true;
  if (rule.required === 'non_fresher') return !form.isFresher;
  if (rule.required === 'optional') {
    return typeof rule.shouldInclude === 'function' ? rule.shouldInclude(form) : false;
  }
  return false;
}

/**
 * Declarative checklist — add new profile fields here to include them in completion %.
 * @type {Array<{ id: string, label: string, sectionId: string, required: 'always'|'non_fresher'|'optional', isComplete: (form: object) => boolean, shouldInclude?: (form: object) => boolean }>}
 */
const PROFILE_COMPLETION_RULES = [
  {
    id: 'photo',
    label: 'Profile photo',
    sectionId: 'profile-section-photo',
    required: 'always',
    isComplete: form => hasText(form.photo)
  },
  {
    id: 'resume',
    label: 'Resume uploaded',
    sectionId: 'profile-section-photo',
    required: 'always',
    isComplete: form => hasText(form.resumeLink || form.resume)
  },
  {
    id: 'profileHeading',
    label: 'Profile heading',
    sectionId: 'profile-section-heading',
    required: 'always',
    isComplete: form => hasText(form.profileHeading)
  },
  {
    id: 'areasOfExpertise',
    label: 'Area of expertise',
    sectionId: 'profile-section-heading',
    required: 'always',
    isComplete: form => (form.areasOfExpertise || []).length > 0
  },
  {
    id: 'name',
    label: 'Full name',
    sectionId: 'profile-section-personal',
    required: 'always',
    isComplete: form => hasText(form.personal?.name || form.name)
  },
  {
    id: 'email',
    label: 'Email',
    sectionId: 'profile-section-basic',
    required: 'always',
    isComplete: form => isValidEmail(form.email || form.contact?.email)
  },
  {
    id: 'phone',
    label: 'Phone',
    sectionId: 'profile-section-basic',
    required: 'always',
    isComplete: form => isValidPhone(form.phone || form.contact?.phone)
  },
  {
    id: 'gender',
    label: 'Gender',
    sectionId: 'profile-section-personal',
    required: 'always',
    isComplete: form => hasText(form.personal?.gender)
  },
  {
    id: 'dob',
    label: 'Date of birth',
    sectionId: 'profile-section-personal',
    required: 'always',
    isComplete: form => Boolean(formatDateOnly(form.personal?.dob))
  },
  {
    id: 'maritalStatus',
    label: 'Marital status',
    sectionId: 'profile-section-personal',
    required: 'always',
    isComplete: form => hasText(form.personal?.maritalStatus)
  },
  {
    id: 'location',
    label: 'City / address',
    sectionId: 'profile-section-personal',
    required: 'always',
    isComplete: form =>
      Boolean(
        hasText(form.personal?.city) ||
          hasText(form.personal?.state) ||
          hasText(form.personal?.address)
      )
  },
  {
    id: 'education',
    label: 'Education',
    sectionId: 'profile-section-education',
    required: 'always',
    isComplete: form => educationComplete(form.educational)
  },
  {
    id: 'experiencePath',
    label: 'Work history or fresher',
    sectionId: 'profile-section-experience',
    required: 'always',
    isComplete: form => form.isFresher || professionalComplete(form.professional)
  },
  {
    id: 'workingModes',
    label: 'Preferred working mode',
    sectionId: 'profile-section-work-prefs',
    required: 'always',
    isComplete: form => (form.preferredWorkingModes || []).length > 0
  },
  {
    id: 'contactChannels',
    label: 'Contact channels',
    sectionId: 'profile-section-work-prefs',
    required: 'always',
    isComplete: form => (form.contactChannels || []).length > 0
  },
  {
    id: 'workLocations',
    label: 'Preferred work locations',
    sectionId: 'profile-section-work-prefs',
    required: 'always',
    isComplete: form => {
      const locations = normalizePreferredWorkLocations(form);
      return locations.length > 0 || hasText(form.location);
    }
  },
  {
    id: 'noticePeriod',
    label: 'Notice period',
    sectionId: 'profile-section-salary',
    required: 'always',
    isComplete: form => isValidNoticePeriod(form.availability)
  },
  {
    id: 'languages',
    label: 'Languages (with proficiency levels)',
    sectionId: 'profile-section-languages',
    required: 'always',
    isComplete: form => languagesComplete(form.languagesKnown)
  },
  {
    id: 'skills',
    label: 'Core competencies',
    sectionId: 'profile-section-skills',
    required: 'non_fresher',
    isComplete: form => (form.skills || []).length > 0
  },
  {
    id: 'technicalSkills',
    label: 'Technical skills',
    sectionId: 'profile-section-technical-skills',
    required: 'non_fresher',
    isComplete: form => (form.technicalSkills || []).length > 0
  },
  {
    id: 'experience',
    label: 'Experience (years)',
    sectionId: 'profile-section-salary',
    required: 'non_fresher',
    isComplete: form => {
      if (!professionalComplete(form.professional)) return false;
      const fromProf = computeExperienceYearsFromProfessional(form.professional);
      if (fromProf != null && fromProf > 0) return true;
      const exp = form.experience ?? form.experienceYears;
      return exp !== '' && exp != null && Number(exp) > 0;
    }
  },
  {
    id: 'currentSalary',
    label: 'Current salary / CTC',
    sectionId: 'profile-section-salary',
    required: 'non_fresher',
    isComplete: form => isValidSalaryCtc(form.currentSalary, { required: true })
  },
  {
    id: 'expectedSalary',
    label: 'Expected salary / CTC',
    sectionId: 'profile-section-salary',
    required: 'optional',
    shouldInclude: form => hasText(form.expectedSalary),
    isComplete: form => isValidSalaryCtc(form.expectedSalary)
  }
];

function getProfileCompletionChecks(candidate, user) {
  const form = normalizeProfileForm(candidate, user);
  if (!form) return [];

  return PROFILE_COMPLETION_RULES.filter(rule => ruleApplies(rule, form)).map(rule => ({
    id: rule.id,
    label: rule.label,
    sectionId: rule.sectionId,
    complete: Boolean(rule.isComplete(form))
  }));
}

function computeProfileCompletion(candidate, user) {
  const checks = getProfileCompletionChecks(candidate, user);
  const completed = checks.filter(c => c.complete).length;
  const total = checks.length;
  const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
  return {
    percent: Math.min(100, Math.max(0, percent)),
    completed,
    total,
    checks,
    needsProfileCompletion: percent < 100
  };
}

function computeProfileCompletionPercent(candidate, user) {
  return computeProfileCompletion(candidate, user).percent;
}

export {
  PROFILE_COMPLETION_RULES,
  normalizeProfileForm,
  getProfileCompletionChecks,
  computeProfileCompletion,
  computeProfileCompletionPercent,
  computeExperienceYearsFromProfessional,
  formatDateOnly,
  languagesComplete
};


